import type { AxiosInstance } from 'axios';
import authService from './auth.service';
import type {
  AcceptProposalResult,
  Campaign,
  CampaignResult,
  CreateCampaignBody,
  CreateProposalBody,
  Proposal,
} from '../types/proposals';

/**
 * Product proposals (0070).
 *
 *   POST /api/v1/clients/{client_id}/proposals          staff
 *   GET  /api/v1/clients/{client_id}/proposals          client self / roster agent / manager
 *   POST /api/v1/proposals/{id}/accept | decline        client
 *   POST /api/v1/proposal-campaigns                     manager
 *   GET  /api/v1/proposal-campaigns                     manager
 */
class ProposalsService {
  private get api(): AxiosInstance {
    return authService.getApi();
  }

  async listForClient(clientId: string): Promise<Proposal[]> {
    const res = await this.api.get<Proposal[]>(`/api/v1/clients/${clientId}/proposals`);
    return res.data;
  }

  async listMine(): Promise<Proposal[]> {
    return this.listForClient(await authService.getMyId());
  }

  async create(clientId: string, body: CreateProposalBody): Promise<{ id: string }> {
    const res = await this.api.post<{ id: string }>(`/api/v1/clients/${clientId}/proposals`, body);
    return res.data;
  }

  /** Accept. For a product without an active contract, start the contract
   *  first and pass its case id. */
  async accept(proposalId: string, contractCaseId: string | null): Promise<AcceptProposalResult> {
    const res = await this.api.post<AcceptProposalResult>(
      `/api/v1/proposals/${proposalId}/accept`,
      { contract_case_id: contractCaseId },
    );
    return res.data;
  }

  async decline(proposalId: string): Promise<void> {
    await this.api.post(`/api/v1/proposals/${proposalId}/decline`);
  }

  async createCampaign(body: CreateCampaignBody): Promise<CampaignResult> {
    const res = await this.api.post<CampaignResult>('/api/v1/proposal-campaigns', body);
    return res.data;
  }

  async listCampaigns(): Promise<Campaign[]> {
    const res = await this.api.get<Campaign[]>('/api/v1/proposal-campaigns');
    return res.data;
  }
}

export default new ProposalsService();
